import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../core/services/auth.service';
import { ErrorHandlerService } from '../../core/services/error-handler.service';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  loading = false;
  submitted = false;
  returnUrl!: string;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    // Se já está autenticado, redireciona para dashboard
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/dashboard']);
    }

    this.loginForm = this.formBuilder.group({
      login: [{ value: '', disabled: false }, Validators.required],
      password: [{ value: '', disabled: false }, Validators.required],
    });

    // Obtém a URL de retorno ou padrão para '/dashboard'
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
  }

  get f() {
    return this.loginForm.controls;
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;

    // Desabilitar os controles quando loading inicia
    this.loginForm.get('login')?.disable();
    this.loginForm.get('password')?.disable();

    const credentials = this.loginForm.getRawValue();
    console.log('[Login] iniciando login para usuario:', credentials.login);

    this.authService.login(credentials).subscribe({
      next: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Login realizado com sucesso!',
          life: 3000,
        });
        this.router.navigate([this.returnUrl]);
      },
      error: (error) => {
        // Garantir que o loading seja encerrado e reabilitar os controles em caso de erro
        this.loading = false;
        this.loginForm.get('login')?.enable();
        this.loginForm.get('password')?.enable();

        // Log dos detalhes do erro para debugging
        console.error('[Login] erro ao autenticar:', error);
        this.errorHandler.logErrorDetails(error);

        // Tratamento especial para 402 (empresa bloqueada/assinatura)
        if (error?.status === 402) {
          const backendMsg = error?.error?.error || error?.error?.message || 'Acesso bloqueado por assinatura.';
          this.messageService.add({
            severity: 'error',
            summary: 'Acesso Bloqueado',
            detail: backendMsg,
            life: 8000,
          });
          return;
        }

        // Tratamento especial para 401 (credenciais inválidas ou problemas de autenticação)
        if (error?.status === 401) {
          // Usar a mensagem do backend se disponível, ou mensagem padrão
          const backendMsg = error?.error?.message || error?.error?.error || '';
          const errorMessage = backendMsg || this.errorHandler.getErrorMessage(error);

          this.messageService.add({
            severity: 'error',
            summary: 'Credenciais Inválidas',
            detail: errorMessage,
            life: 5000,
          });
          return;
        }

        // Obter mensagem de erro apropriada para outros casos
        const errorMessage = this.errorHandler.getErrorMessage(error);
        const severity = this.errorHandler.isNetworkError(error) ? 'warn' : 'error';

        this.messageService.add({
          severity: severity,
          summary: 'Erro de Login',
          detail: errorMessage,
          life: 6000,
          sticky: this.errorHandler.isNetworkError(error), // Erro de rede fica mais tempo
        });


        // Mostrar ação de recuperação em casos específicos
        if (this.errorHandler.isNetworkError(error)) {
          const recoveryAction = this.errorHandler.getRecoveryAction(error);
          this.messageService.add({
            severity: 'info',
            summary: 'Ação Sugerida',
            detail: recoveryAction,
            life: 6000,
          });
        }
      },
    });
  }
}
